import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { UserCheck, ArrowUpRight, ShieldCheck } from "lucide-react";
import { t } from "@/lib/i18n";

/**
 * Curated links to state radon programs and AARST/NRPP national directories.
 * State list is intentionally minimal — expand as program content becomes available.
 */
const STATE_LINKS = {
  PA: { name: t("PA — DEP Radon Division"), url: "https://www.dep.pa.gov/Business/RadiationProtection/RadonDivision/Pages/default.aspx" },
  OH: { name: t("OH — Radon Program"), url: "https://odh.ohio.gov/know-our-programs/radon" },
  NY: { name: t("NY — Bureau of Environmental Radiation Protection"), url: "https://www.health.ny.gov/environmental/radiological/radon/" },
  CA: { name: t("CA — CDPH Radon Program"), url: "https://www.cdph.ca.gov/Programs/CEH/DRSEM/Pages/EMB/Radon/Radon.aspx" },
  FL: { name: t("FL — DOH Radon and Indoor Air"), url: "https://www.floridahealth.gov/environmental-health/radon/index.html" },
  IL: { name: t("IL — Emergency Management Agency Radon Program"), url: "https://iema.illinois.gov/nuclearsafety/radon.html" },
};

const CertifiedProfessional = ({ state }) => {
  const link = STATE_LINKS[state?.toUpperCase()];
  const [verified, setVerified] = useState([]);
  useEffect(() => {
    axios.get(`${API}/mitigators`).then((r) => setVerified(r.data)).catch(() => {});
  }, []);

  return (
    <div className="card-flat" data-testid="pros-card">
      <div className="flex items-center gap-2 mb-2">
        <UserCheck size={16} className="text-[#2F524A]" />
        <h3 className="font-serif text-xl">{t("Find a certified professional")}</h3>
      </div>
      <p className="text-sm text-[#5C6661]">
        {t("Look up qualified radon testers and mitigators. The national directories cover every state.")}
      </p>
      <ul className="mt-4 space-y-2 text-sm">
        <li>
          <a className="inline-flex items-center gap-1 text-[#2F524A] hover:underline font-semibold"
             href="https://aarst-nrpp.com/wp/certification-listing/" target="_blank" rel="noopener noreferrer"
             data-testid="pros-nrpp-link">
            {t("AARST-NRPP Certified Professionals")}{" "}<ArrowUpRight size={13} />
          </a>
        </li>
        <li>
          <a className="inline-flex items-center gap-1 text-[#2F524A] hover:underline font-semibold"
             href="https://nrsb.org/find-a-pro/" target="_blank" rel="noopener noreferrer"
             data-testid="pros-nrsb-link">
            {t("NRSB Find a Pro")}{" "}<ArrowUpRight size={13} />
          </a>
        </li>
        {link && (
          <li>
            <a className="inline-flex items-center gap-1 text-[#2F524A] hover:underline font-semibold"
               href={link.url} target="_blank" rel="noopener noreferrer"
               data-testid="pros-state-link">
              {link.name} <ArrowUpRight size={13} />
            </a>
          </li>
        )}
      </ul>

      {verified.length > 0 && (
        <div className="mt-6 pt-6 border-t border-[#E8ECEA]" data-testid="verified-mitigators">
          <p className="eyebrow flex items-center gap-1 text-[#2F524A]">
            <ShieldCheck size={12} />{" "}{t("Community-verified mitigators")}
          </p>
          <p className="text-xs text-[#5C6661] mt-1">
            {t("Contractors who completed mitigations followed by a post-test on our platform.")}
          </p>
          <ul className="mt-3 space-y-2">
            {verified.map((m) => (
              <li key={m.contractor} className="flex items-center justify-between text-sm gap-2"
                data-testid="verified-mitigator-row">
                <div>
                  <p className="text-[#1C201F] font-semibold">{m.contractor}</p>
                  <p className="text-[10px] text-[#5C6661]">
                    {m.states.filter(Boolean).join(", ") || "—"} · {m.jobs} verified job{m.jobs === 1 ? "" : "s"}
                  </p>
                </div>
                {m.avg_cost && (
                  <span className="text-xs text-[#5C6661]">
                    ~${Math.round(m.avg_cost).toLocaleString()}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CertifiedProfessional;
